import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Deal, InvestorOfferingSummary } from '@bcc/shared';
import { DEAL_STATUSES } from '@bcc/shared';
import { SITE_GLASS_CARD } from '../constants/siteUi';
import { apiFetch } from '../lib/api';
import { resolveDealCoverUrl } from '../lib/dealCover';
import { useAuth } from '../contexts/AuthContext';

const STATE_FILTERS = [
  { label: 'Alabama', test: /\b(AL|ALABAMA)\b/i },
  { label: 'Arizona', test: /\b(AZ|ARIZONA)\b/i },
  { label: 'Florida', test: /\b(FL|FLORIDA)\b/i },
  { label: 'Georgia', test: /\b(GA|GEORGIA)\b/i },
  { label: 'Virginia', test: /\b(VA|VIRGINIA)\b/i },
  { label: 'Maryland', test: /\b(MD|MARYLAND)\b/i },
  { label: 'Texas', test: /\b(TX|TEXAS)\b/i },
] as const;

function dealTextBlob(d: Deal) {
  return `${d.title}\n${d.description}`;
}

function regionForDeal(d: Deal): string | null {
  if (d.state) {
    const st = d.state.trim();
    for (const { label, test } of STATE_FILTERS) {
      if (test.test(st)) return label;
    }
    return st;
  }
  const t = dealTextBlob(d);
  for (const { label, test } of STATE_FILTERS) {
    if (test.test(t)) return label;
  }
  return null;
}

function locationLine(d: Deal): string {
  if (d.city && d.state) return `${d.city}, ${d.state}`;
  const t = `${d.title} · ${d.description}`;
  const m = t.match(/([A-Z][a-z]+(?:\s[A-Z][a-z]+)*),\s*(AL|AK|AZ|AR|CA|CO|FL|GA|MD|TX|NY|NJ|TN|NC|SC|VA)/);
  if (m) return `${m[1]}, ${m[2]}`;
  const r = regionForDeal(d);
  return r ? `${r}` : 'U.S. private placement';
}

function badgeLabel(d: Deal): string {
  const t = d.title.trim();
  if (t.length <= 42) return t;
  return `${t.slice(0, 40)}…`;
}

const CARD_SHELL =
  'group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 shadow-[0_24px_60px_-30px_rgb(0_0_0/0.8)] outline-none ring-0 transition hover:-translate-y-0.5 hover:border-white/20 focus-visible:ring-2 focus-visible:ring-[var(--header-shell-bg)]';

function statusPill(status: Deal['status']) {
  const open = status === DEAL_STATUSES.OPEN;
  return (
    <span
      className={`rounded-md px-2 py-0.5 font-display text-[0.55rem] font-semibold uppercase tracking-[0.08em] ring-1 backdrop-blur-sm ${
        open
          ? 'bg-emerald-500/25 text-emerald-100 ring-emerald-400/35'
          : 'bg-white/10 text-white/85 ring-white/15'
      }`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

function OfferingCard({
  row,
  condition,
  strategicTag,
}: {
  row: InvestorOfferingSummary;
  condition?: string;
  strategicTag?: string;
}) {
  const d = row.deal;
  const cover = resolveDealCoverUrl(d);
  const showTagColumn = Boolean(condition || strategicTag);
  const hasPosition = row.invested > 0;

  return (
    <Link to={`/deals/${d.id}`} className={`${CARD_SHELL} min-h-[280px]`}>
      <div className="relative aspect-[16/11] w-full shrink-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-[#1a2520] via-[#243028] to-[var(--home-zone-void)]"
          style={
            cover
              ? {
                  backgroundImage: `linear-gradient(to top, rgb(10 18 36 / 0.88) 0%, rgb(10 18 36 / 0.2) 45%, transparent 72%), url(${cover})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
        />
        <div
          className={`absolute left-3 top-3 max-w-[min(72%,240px)] ${showTagColumn ? 'sm:max-w-[min(58%,200px)]' : ''}`}
        >
          <span className="inline-block rounded-md bg-neutral-950/75 px-2.5 py-1 font-display text-[0.58rem] font-semibold uppercase leading-tight tracking-[0.04em] text-white ring-1 ring-white/10 backdrop-blur-sm">
            {badgeLabel(d)}
          </span>
        </div>
        <div className="absolute right-3 top-3 flex flex-col items-end gap-1.5">{statusPill(d.status)}</div>
        {showTagColumn ? (
          <div className="absolute right-3 top-12 flex max-w-[min(52%,220px)] flex-col items-end gap-1.5 text-right">
            {condition ? (
              <span className="rounded-md bg-black/55 px-2.5 py-1 font-display text-[0.56rem] font-semibold uppercase leading-tight tracking-[0.05em] text-white ring-1 ring-white/15 backdrop-blur-sm">
                {condition}
              </span>
            ) : null}
            {strategicTag ? (
              <span className="rounded-md bg-[var(--header-shell-bg)]/92 px-2.5 py-1 font-display text-[0.56rem] font-semibold uppercase leading-tight tracking-[0.05em] text-white shadow-md shadow-black/20 ring-1 ring-white/25 backdrop-blur-sm">
                {strategicTag}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-4 pb-3 pt-12">
          <p className="font-body text-[0.8125rem] font-medium tracking-wide text-white/85">{locationLine(d)}</p>
          {d.property_type || d.strategy ? (
            <p className="mt-1 font-body text-[0.65rem] font-medium uppercase tracking-wide text-white/70">
              {[d.property_type, d.strategy].filter(Boolean).join(' · ')}
            </p>
          ) : null}
          <p className="mt-1.5 font-display text-lg font-bold leading-tight uppercase tracking-[0.04em] text-white drop-shadow-sm md:text-xl">
            {d.title}
          </p>
        </div>
      </div>
      <div className="mt-auto border-t border-white/[0.08] bg-[var(--home-zone-dark)]/80 px-4 py-3">
        <p className="font-mono text-[0.58rem] font-medium uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">
          Your position
        </p>
        <div className="mt-2 grid grid-cols-2 gap-3 font-body text-sm">
          <div>
            <p className="text-[0.65rem] text-[var(--home-muted-on-dark)]">Invested</p>
            <p className="font-display text-base font-semibold text-[var(--home-text-on-dark)]">
              {hasPosition ? `$${row.invested.toLocaleString()}` : '—'}
            </p>
            {!hasPosition ? (
              <p className="mt-0.5 text-[0.65rem] text-[var(--home-muted-on-dark)]">Not subscribed</p>
            ) : row.positions > 1 ? (
              <p className="mt-0.5 text-[0.6rem] text-[var(--home-muted-on-dark)]">{row.positions} subscriptions</p>
            ) : null}
          </div>
          <div>
            <p className="text-[0.65rem] text-[var(--home-muted-on-dark)]">Distributions</p>
            <p className="font-display text-base font-semibold text-emerald-300/95">
              {row.returns > 0 ? `$${row.returns.toLocaleString()}` : hasPosition ? '$0' : '—'}
            </p>
            {!hasPosition ? (
              <p className="mt-0.5 text-[0.65rem] text-[var(--home-muted-on-dark)]">—</p>
            ) : null}
          </div>
        </div>
        <p className="mt-2 text-center text-[0.65rem] text-[var(--home-miami-blue)]">View offering →</p>
      </div>
    </Link>
  );
}

/** Title-keyed tags aligned with seeded demo deals (see migration 008). */
const SEEDED_OFFERING_TAGS: Record<string, { condition: string; strategicTag: string }> = {
  'River District I': { condition: 'Class A', strategicTag: 'Core' },
  'Sonoran Ridge': { condition: 'Class A', strategicTag: 'Cash Flow' },
  'Coastal Commerce Lofts': { condition: 'Class B', strategicTag: 'Value-Add' },
  'Midtown Annex': { condition: 'Class B', strategicTag: 'Appreciation' },
  'Potomac Quarter': { condition: 'Class C', strategicTag: 'Core Plus' },
  'Harbor Works': { condition: 'Class C', strategicTag: 'Tax Advantage' },
  'Austin Tech Corridor': { condition: 'Class D', strategicTag: 'Opportunistic' },
  'Cottonwood Commons': { condition: 'Class B', strategicTag: 'Income-Producing' },
  'Camelback Midrise': { condition: 'Class A', strategicTag: 'Institutional Grade' },
  'Lake Eola Reserve': { condition: 'Class B', strategicTag: 'Core Plus' },
  'Energy Corridor Plaza': { condition: 'Class C', strategicTag: 'Repositioning' },
  'James River Landing': { condition: 'Class D', strategicTag: 'Redevelopment' },
};

export function InvestorOfferings() {
  const { session, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<InvestorOfferingSummary[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchGen = useRef(0);
  const token = session?.access_token;

  useEffect(() => {
    if (!token) return;
    const gen = ++fetchGen.current;
    (async () => {
      try {
        const res = await apiFetch<{ offerings: InvestorOfferingSummary[] }>('/api/investor/offerings', {
          token,
        });
        if (fetchGen.current !== gen) return;
        setRows(res.offerings);
        setErr(null);
      } catch (e) {
        if (fetchGen.current !== gen) return;
        setErr((e as Error).message);
      } finally {
        if (fetchGen.current === gen) setLoading(false);
      }
    })();
  }, [token]);

  if (authLoading) {
    return (
      <div className={`${SITE_GLASS_CARD} p-8 font-body text-sm text-[var(--home-muted-on-dark)]`}>
        Loading offerings…
      </div>
    );
  }

  if (!token) {
    return null;
  }

  if (loading) {
    return (
      <div className={`${SITE_GLASS_CARD} p-8 font-body text-sm text-[var(--home-muted-on-dark)]`}>
        Loading offerings…
      </div>
    );
  }

  if (err) {
    return (
      <div className={`${SITE_GLASS_CARD} p-8 font-body text-sm text-rose-200`}>
        <p className="font-semibold text-rose-100">Could not load offerings</p>
        <p className="mt-2 text-[var(--home-muted-on-dark)]">{err}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className={`${SITE_GLASS_CARD} space-y-2 p-6 md:p-8`}>
        <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--home-miami-blue)]">
          Offerings
        </p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--home-text-on-dark)] md:text-3xl">
          Your programs
        </h1>
        <p className="font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
          Live and historical deals available to you. Each card shows your committed capital and recorded distributions
          (payouts) for that program. Open a card for documents and subscription.
        </p>
      </header>

      {rows.length === 0 ? (
        <p className="font-body text-sm text-[var(--home-muted-on-dark)]">No offerings published yet.</p>
      ) : (
        <ul className="grid gap-7 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map((row) => {
            const tags = SEEDED_OFFERING_TAGS[row.deal.title];
            return (
              <li key={row.deal.id}>
                <OfferingCard row={row} condition={tags?.condition} strategicTag={tags?.strategicTag} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
